"use client";

import { useGetDietAddons } from "@repo/api/catalog/api/diet-addon-group";
import { format, max, min } from "date-fns";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useStore } from "zustand";
import { SectionHero } from "@/components/common/section-hero";
import { Form } from "@/components/ui/form";
import { toast } from "@/components/ui/sonner";
import { AddonsConfigProvider } from "@/features/addons/components/addons-config-provider";
import { buildAddonGtmItemsForCartItem } from "@/features/addons/lib/addon-gtm-item";
import {
  estimateProductsPricing,
  mergeAddonsPricing,
  productEntries,
  productItemsToAddons,
  splitAddonEntries,
} from "@/features/addons/lib/product-items";
import {
  addonsToSubItems,
  subItemsToAddons,
} from "@/features/addons/lib/sub-items";
import { getAddonsErrorMessage } from "@/features/addons/lib/sub-items-error";
import { createAddonsConfigStore } from "@/features/addons/store/addons-config-store";
import { sendEvent } from "@/features/analytics/gtm/event";
import { getDietWithAddonsGtmValue } from "@/features/analytics/lib/gtm-value";
import { useUpdateCart } from "@/features/cart/api/hooks/use-update-cart";
import { useConfigureDietForm } from "@/features/cart/forms/configure-diet";
import { CALENDAR_SECTION_ID } from "@/features/cart/helpers/configure-sections";
import { productItemsForItem } from "@/features/cart/helpers/product-items";
import type {
  Cart,
  CartProductItem,
  ConfigurationItem,
} from "@/features/cart/types";
import { getUnavailableProductId } from "@/features/cart/utils/unavailable-product-error";
import {
  getCartItemOmnibusPresentation,
  isOmnibusEnabled,
  type OmnibusConfiguredPrices,
} from "@/features/diets/helpers/omnibus";
import type {
  DietCatalogCateringType,
  DietCatalogDetail,
} from "@/features/diets/types";
import type { DietSearchResultsItemType } from "@/features/diets/types/diet-search-resilts.type";
import type { Globals } from "@/features/globals/types/globals.type";
import { ApiError } from "@/lib/helpers/errors";
import { useItemPrice } from "../../hooks/use-item-price";
import type { ItemPriceBody } from "../../types/item-price-body.type";
import { useCartStore } from "../cart-provider";
import { ConfigurationBar } from "./configuration-bar";
import { ConfigurationPricing } from "./configuration-pricing";
import { ConfigureDietForm } from "./configure-diet-form";

const DEFAULT_CONFIGURATION_DAYS = 15;

// Deep link to the calendar section: the blocks above it keep changing height
// while the view hydrates, so the alignment is repeated across that window
// instead of once.
const SCROLL_ALIGN_INTERVAL_MS = 150;
const SCROLL_ALIGN_ATTEMPTS = 8;

// Events that mean the user took over scrolling; `scroll` is not among them
// because the alignment itself raises it.
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown"] as const;

const getDietDefaultVariant = (diet: DietCatalogDetail) => {
  const baseVariantId = diet.base.variant;
  let mealTypesIds: string[] = [];
  let calories = 0;

  // in case if diet variant exists
  if (baseVariantId) {
    const baseVariant = diet.variants?.find(
      (variant) => variant.id === baseVariantId,
    );

    if (!baseVariant) return null;

    mealTypesIds = baseVariant.mealTypes.map((mealType) => mealType.id);
    calories = diet.base.caloricity;
  } else {
    // in case if diet supervariant exists
    const calorifics = diet.calorifics.find(
      (cal) => cal.calories === diet.base.caloricity,
    );

    const meals = calorifics?.mealsCaloricValue ?? [];
    const lowestCalories = meals.length
      ? meals.reduce((min, current) =>
          current.caloricity < min.caloricity ? current : min,
        )
      : undefined;

    mealTypesIds = lowestCalories ? [lowestCalories.mealId] : [];
    calories = lowestCalories?.caloricity || 0;
  }

  return {
    merchantMealsIds: mealTypesIds,
    calories,
  };
};

// The edited position's whole selection: the catering's addons from its
// sub-items plus the Foodango products the cart keeps apart (MPL-3829).
const getSeedAddons = (
  diet: DietCatalogDetail,
  configurationItem?: ConfigurationItem,
  productItems: CartProductItem[] = [],
) =>
  configurationItem && configurationItem.dietId === diet.id
    ? [
        ...subItemsToAddons(configurationItem.subItems),
        ...productItemsToAddons(productItems),
      ]
    : [];

export const ConfigureDietPage = ({
  configurationItem,
  configuration,
  diet,
  globals,
  productItems,
}: {
  configurationItem?: ConfigurationItem;
  configuration?: DietSearchResultsItemType | null;
  diet: DietCatalogDetail;
  globals?: Globals | null;
  // Products of the edited position as the cart holds them (edit flow only).
  productItems?: CartProductItem[];
}) => {
  const [isSaving, startTransition] = useTransition();
  const cartStore = useCartStore((state) => state);
  const router = useRouter();
  const t = useTranslations();
  const locale = useLocale();
  const pricingRef = useRef<HTMLDivElement>(null);

  // View-local addon store, seeded from the edited item's addons. Shared with
  // the addons modal (via provider) and read here on save. Not persisted: addon
  // choices stay on the configuration view until the diet is added / saved.
  const [addonsStore, setAddonsStore] = useState(() =>
    createAddonsConfigStore(
      getSeedAddons(diet, configurationItem, productItems),
      diet.id ?? null,
    ),
  );

  if (addonsStore.getState().dietId !== (diet.id ?? null)) {
    setAddonsStore(
      createAddonsConfigStore(
        getSeedAddons(diet, configurationItem, productItems),
        diet.id ?? null,
      ),
    );
  }

  const { data: addonCategories, isSuccess: areAddonsLoaded } =
    useGetDietAddons(diet.id ?? "", undefined, {
      query: {
        enabled: Boolean(diet.id),
        staleTime: Number.POSITIVE_INFINITY,
        select: (response) => response.data,
      },
    });

  const dietMerchantId = diet.catering?.id ?? "";

  // Reactive view of the addon selection, split the way the cart stores it:
  // the catering's addons are sub-items of the position, products of other
  // merchants go to the cart's product collection (MPL-3829). Whenever it
  // changes, the price query below refetches with the current sub-items so the
  // quote stays accurate.
  const addonEntries = useStore(addonsStore, (state) => state.addons);
  const { subItems, productItems: productSelection } = useMemo(
    () =>
      splitAddonEntries({
        entries: addonEntries,
        categories: addonCategories ?? [],
        dietMerchantId,
      }),
    [addonEntries, addonCategories, dietMerchantId],
  );

  const variant =
    (!configurationItem &&
      !configuration &&
      getDietDefaultVariant(diet as unknown as DietCatalogDetail)) ||
    null;

  const maxDays = diet.catering?.days?.max ?? 99;
  // A catering selling fewer days than the default would open the form already
  // failing its own limit.
  const defaultDays = configuration
    ? Math.min(DEFAULT_CONFIGURATION_DAYS, maxDays)
    : 1;

  const form = useConfigureDietForm({
    defaults: configurationItem || {
      mealIds:
        configuration?.merchantMealsIds ?? variant?.merchantMealsIds ?? [],
      calories: configuration?.calories ?? diet.base?.caloricity ?? 0,
      dates: undefined,
      sourceCalorifics: configuration?.sourceCalorifics,
      days: defaultDays,
    },

    diet,
    min: diet.catering?.days?.min ?? 1,
    max: maxDays,
    minMeals: diet.minMealTypes,
    locale,
  });

  const { isSubmitSuccessful, isSubmitted, isSubmitting, errors } =
    form.formState;

  const handleSave = () => {
    const item = configurationItem ? { id: configurationItem.id } : {};
    const url = item.id ? "/api/cart/update" : "/api/cart/add";

    const body: ItemPriceBody = {
      calories: +form.getValues("kcal"),
      mealIds: form.getValues("meals") ?? [],
      dates: form
        .getValues("dates")
        .map((date: Date) => format(date, "yyyy-MM-dd")),
      dietId: diet.id ?? "",
      merchantId: diet.catering?.id ?? "",
    };

    startTransition(() => {
      if (item.id) {
        cartStore.addPendingEditItem(body, item.id);
      } else {
        cartStore.addPendingItem(body);
      }
      router.push("/cart");

      fetch(url, {
        method: "POST",
        body: JSON.stringify({
          cartId: cartStore.cartId,
          item: {
            ...configurationItem,
            ...item,
            ...body,
            subItems,
            parameters: {
              ecoContainers: false,
            },
          },
          productItems: productSelection,
        }),
      })
        .then(async (res: Response) => {
          if (!res.ok) {
            // The cart route forwards the upstream body, so a rejected addon
            // selection (422) can be reported for what it is.
            const body = await res.json().catch(() => null);
            throw new ApiError("Failed to add to cart", res.status, body);
          }

          const response = res.json() as Promise<{
            cartId: string;
            cart: Cart;
            itemId?: string; // only for add
          }>;

          const { cartId } = await response;

          const selectedDates = form.getValues("dates") || [];
          const totalPrice = data?.pricing?.final.gross || 0;
          const dailyPrice = totalPrice / (selectedDates.length || 1);

          const firstDate = min(selectedDates || []);
          const lastDate = max(selectedDates || []);

          if (data && diet) {
            const addonItems = buildAddonGtmItemsForCartItem({
              cateringName: diet.catering?.name,
              categories: addonCategories ?? [],
              // Every selected entry, products included — they are
              // addons of the same catalog for analytics.
              subItems: addonsToSubItems(addonEntries),
              // The quote every summary of this view prices the selection
              // with, so `price` is the discounted one, deposit excluded.
              subItemsPricing,
            });

            sendEvent({
              event: "add_to_cart",
              ecommerce: {
                cartId,
                currency: "PLN",
                value: getDietWithAddonsGtmValue({
                  dietTotal: totalPrice,
                  addonItems,
                }),
                items: [
                  {
                    item_id: diet.id as string,
                    item_name: diet.name,
                    item_brand: diet.catering?.name as string,
                    item_variant: diet?.menuSelection
                      ? "diet with choice"
                      : "diet",
                    item_category2: String(form.getValues("kcal")),
                    item_category3: firstDate.getTime(),
                    item_category4: lastDate.getTime(),
                    item_category: diet?.tags.map((tag) => tag.slug).join(", "),
                    price: dailyPrice,
                    quantity: 1,
                    duration: selectedDates.length,
                  },
                  ...addonItems,
                ],
              },
            });
          }

          return response;
        })
        .then(({ cartId, cart, itemId }) => {
          cartStore.updateCart(cartId, cart, "update", t);
          if (!item.id && itemId) {
            cartStore.addDeliveryCost(
              itemId,
              diet.id as string,
              diet.catering
                ?.deliveryCosts as unknown as DietCatalogCateringType["deliveryCosts"],
            );
          }
        })
        .catch((error: unknown) => {
          // A product that left the offer is dropped from the selection so the
          // next attempt goes through (MPL-3829); anything else is reported.
          const unavailableProductId = getUnavailableProductId(error);
          if (unavailableProductId) {
            addonsStore.getState().setAddonEntries(unavailableProductId, []);
            toast.error(t("cart.addons.errors.productRemoved"));
            return;
          }
          toast.error(getAddonsErrorMessage(error, t));
        });
    });
  };

  const { data, isPending, isFetching } = useItemPrice({
    dietId: diet.id as string,
    merchantId: diet.catering?.id as string,
    form,
    subItems,
  });

  // Diet-only pricing and the addon quote of the position arrive separately and
  // stay separate: the diet price is the discount base, the addon value is what
  // the cart total adds on top (MPL-3694).
  const pricing = data?.pricing ?? undefined;
  // Omnibus (MPL-1835): L of this quote, i.e. for the configured number of
  // days, against the diet's reduced price per day. The views use it only with
  // the Omnibus flag on.
  const omnibus = pricing
    ? getCartItemOmnibusPresentation({
        pricing,
        days: form.watch("dates")?.length ?? 0,
        lowestPrice: data?.lowestPriceLast30Days,
      })
    : undefined;
  // The menu preview opened from the diet card repeats the summary's per-day
  // price and L. Only under the flag: without it the preview keeps its own price.
  const daysCount = form.watch("dates")?.length || 1;
  const menuPrices: OmnibusConfiguredPrices | undefined =
    pricing && isOmnibusEnabled()
      ? {
          price: (pricing.before?.gross ?? pricing.final.gross) / daysCount,
          promoPrice: pricing.final.gross / daysCount,
          lowestPrice: omnibus?.promotion ? omnibus.lowestPrice : null,
        }
      : undefined;

  // The price endpoint quotes the diet and its sub-items only. The products are
  // quoted by the cart (edit flow) and estimated from the catalog until then,
  // and join the addon quote so every summary prices the selection as one.
  const productsPricing = useMemo(
    () =>
      estimateProductsPricing({
        entries: productEntries({
          entries: addonEntries,
          categories: addonCategories ?? [],
          dietMerchantId,
        }),
        categories: addonCategories ?? [],
        quoted: productItems,
      }),
    [addonEntries, addonCategories, dietMerchantId, productItems],
  );
  const subItemsPricing = useMemo(
    () => mergeAddonsPricing(data?.subItemsPricing, productsPricing),
    [data?.subItemsPricing, productsPricing],
  );

  // A rollback writes to the addon store too; without this guard the resulting
  // store change would be persisted straight back through PUT.
  const isRollingBackAddonsRef = useRef(false);

  // Sub-items are validated before the cart is written, so a rejection leaves it
  // untouched (MPL-3694). Restore the selection the cart still holds — the store
  // is the freshest copy of it, with the server-rendered item as the fallback.
  const rollbackAddons = useCallback(() => {
    const persisted =
      cartStore.items.find(
        (cartItem) => cartItem.configurationItem.id === configurationItem?.id,
      )?.configurationItem.subItems ?? configurationItem?.subItems;
    const persistedProducts = productItemsForItem(
      cartStore.productItems,
      configurationItem?.id,
    );

    isRollingBackAddonsRef.current = true;
    addonsStore
      .getState()
      .seedAddons([
        ...subItemsToAddons(persisted),
        ...productItemsToAddons(persistedProducts),
      ]);
    isRollingBackAddonsRef.current = false;
  }, [addonsStore, cartStore.items, cartStore.productItems, configurationItem]);

  // See useCartItemAddons: a product without a price is dropped from the
  // selection — silently when the route already wrote the cart without it,
  // through a re-persist when the write was rejected.
  const dropUnavailableProduct = useCallback(
    (productId: string, persist: boolean) => {
      isRollingBackAddonsRef.current = !persist;
      addonsStore.getState().setAddonEntries(productId, []);
      isRollingBackAddonsRef.current = false;
      toast.info(t("cart.addons.errors.productRemoved"));
    },
    [addonsStore, t],
  );

  const { mutate: updateCart } = useUpdateCart({
    onSuccess: ({ cartId, cart, droppedProductId }) => {
      cartStore.updateCart(cartId, cart, "update", t);
      if (droppedProductId) {
        dropUnavailableProduct(droppedProductId, false);
      }
    },
    onError: (error) => {
      const unavailableProductId = getUnavailableProductId(error);
      if (unavailableProductId) {
        rollbackAddons();
        dropUnavailableProduct(unavailableProductId, true);
        return;
      }
      toast.error(getAddonsErrorMessage(error, t));
      rollbackAddons();
    },
  });

  // Persist the current addon selection to the cart the moment it changes.
  // Only for an item that already lives in the cart (edit flow): the diet is
  // then a real position we can PUT to. A brand-new diet is not in the cart yet
  // and its addons stay view-local until the main "add to cart" CTA.
  const persistAddons = useCallback(() => {
    if (!configurationItem?.id || isRollingBackAddonsRef.current) {
      return;
    }

    // Read straight from the store: this runs as a subscription callback, so
    // the memoized render-scoped split could be one emission behind.
    const current = splitAddonEntries({
      entries: addonsStore.getState().getAllAddons(),
      categories: addonCategories ?? [],
      dietMerchantId,
    });

    updateCart({
      cartId: cartStore.cartId,
      item: {
        ...configurationItem,
        calories: +form.getValues("kcal"),
        mealIds: form.getValues("meals") ?? [],
        dates: form
          .getValues("dates")
          .map((date: Date) => format(date, "yyyy-MM-dd")),
        dietId: diet.id ?? "",
        merchantId: diet.catering?.id ?? "",
        subItems: current.subItems,
        parameters: { ecoContainers: false },
      },
      productItems: current.productItems,
    });
  }, [
    addonsStore,
    addonCategories,
    configurationItem,
    cartStore.cartId,
    diet,
    dietMerchantId,
    form,
    updateCart,
  ]);

  // The store only changes when the user commits an addon selection (add / edit
  // / remove / remove-all), never on the initial seed, so every emission is a
  // real change worth persisting.
  useEffect(() => {
    if (!configurationItem?.id) {
      return;
    }
    return addonsStore.subscribe(persistAddons);
  }, [addonsStore, persistAddons, configurationItem?.id]);

  // Whenever the diet days change, drop addon selections attached to days that
  // are no longer part of the diet. This keeps every sub-item date within the
  // item's `dates` (backend rejects the rest with a 422) and, in the edit flow,
  // the resulting store change persists the trimmed selection to the cart.
  const dietDatesSignature = (form.watch("dates") ?? [])
    .map((date: Date) => format(date, "yyyy-MM-dd"))
    .join("|");
  useEffect(() => {
    const dateKeys = dietDatesSignature ? dietDatesSignature.split("|") : [];
    addonsStore.getState().pruneToDates(dateKeys);
  }, [addonsStore, dietDatesSignature]);

  const addonCatalogSignature = (addonCategories ?? [])
    .flatMap((category) => category.addons.map((addon) => addon.id))
    .join("|");
  useEffect(() => {
    if (!areAddonsLoaded) {
      return;
    }
    const addonIds = addonCatalogSignature
      ? addonCatalogSignature.split("|")
      : [];
    addonsStore.getState().pruneToAddonIds(addonIds);
  }, [addonsStore, areAddonsLoaded, addonCatalogSignature]);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful) {
      if (typeof document !== "undefined") {
        if (errors.meals) {
          document
            .getElementById("cart-meals")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (errors.dates) {
          document
            .getElementById(CALENDAR_SECTION_ID)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  }, [isSubmitSuccessful, isSubmitted, isSubmitting]);

  // Entered from the cart's "Termin dostawy niedostępny" alert, which links
  // straight to the calendar. The browser's own hash jump fires during
  // hydration, while the blocks above the calendar still settle, so it lands
  // hundreds of pixels off. The alignment is therefore repeated across that
  // window. `instant` is explicit: the global `scroll-behavior: smooth` would
  // otherwise animate each correction and they would fight each other.
  useEffect(() => {
    if (window.location.hash !== `#${CALENDAR_SECTION_ID}`) {
      return;
    }

    let attempts = 0;
    let timer: ReturnType<typeof setInterval>;

    const stopAligning = () => clearInterval(timer);

    const align = () => {
      document
        .getElementById(CALENDAR_SECTION_ID)
        ?.scrollIntoView({ behavior: "instant", block: "start" });
      attempts += 1;
      if (attempts >= SCROLL_ALIGN_ATTEMPTS) {
        stopAligning();
      }
    };

    align();
    timer = setInterval(align, SCROLL_ALIGN_INTERVAL_MS);

    for (const event of USER_SCROLL_EVENTS) {
      window.addEventListener(event, stopAligning, { passive: true });
    }

    return () => {
      stopAligning();
      for (const event of USER_SCROLL_EVENTS) {
        window.removeEventListener(event, stopAligning);
      }
    };
  }, []);

  return (
    <AddonsConfigProvider store={addonsStore}>
      <Form {...form}>
        <form
          className="flex flex-col lg:flex-row lg:gap-10"
          data-pw="configure-diet-form"
          id="configure-diet-form"
          onSubmit={form.handleSubmit(handleSave)}
        >
          <SectionHero className="min-w-0 flex-1">
            <ConfigureDietForm
              diet={diet}
              form={form}
              globals={globals}
              loading={isPending || isFetching}
              menuPrices={menuPrices}
              pricing={pricing}
              subItemsPricing={subItemsPricing}
            />
          </SectionHero>
          <div
            className="sticky top-28 flex-1 max-md:container lg:w-76 lg:flex-none lg:self-start"
            ref={pricingRef}
          >
            <ConfigurationPricing
              diet={diet as unknown as DietCatalogDetail}
              edit={!!configurationItem}
              form={form}
              loading={isPending || isFetching}
              omnibus={omnibus}
              pending={isSaving}
              pricing={pricing}
              subItemsPricing={subItemsPricing}
            />
          </div>
          <ConfigurationBar
            days={form.watch("dates")?.length}
            disabled={isSaving}
            edit={!!configurationItem}
            loading={isPending || isFetching}
            omnibus={omnibus}
            pricing={pricing}
            pricingRef={pricingRef as RefObject<HTMLDivElement>}
          />
        </form>
      </Form>
    </AddonsConfigProvider>
  );
};
